import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { ReliabilityDescription, reliabilityApi } from '../../services/reliabilityApi';
import { ReliabilityDialog, summarizeReliability } from './ReliabilityDialog';

interface Props {
  /** Any version id (or original_uuid) of the step's function. Omit for steps whose function doesn't exist yet. */
  functionId?: string | null;
  functionName?: string;
  /** The chain's own settings, when saved: used to explain what a step without settings inherits. */
  chainDesc?: ReliabilityDescription | null;
  actAsPublic?: boolean;
}

/**
 * One-line view of the reliability that applies to a chain step, with a button to edit the
 * step function's settings. Retries, backups and the time limit live on the function (or on the
 * chain, as defaults for steps without their own), never on the step itself.
 */
export const StepReliability: React.FC<Props> = ({ functionId, functionName, chainDesc, actAsPublic }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => reliabilityApi(getIdToken, !!actAsPublic), [getIdToken, actAsPublic]);
  const [desc, setDesc] = useState<ReliabilityDescription | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setDesc(null);
    setUnavailable(false);
    if (!functionId) return;
    let cancelled = false;
    api
      .get('function', functionId)
      .then((d) => !cancelled && setDesc(d))
      .catch(() => !cancelled && setUnavailable(true));
    return () => {
      cancelled = true;
    };
  }, [api, functionId]);

  const chainApplies = !!chainDesc && !chainDesc.is_standard && chainDesc.settings.apply_to_steps;
  let label: string;
  let detail: string;
  if (!functionId) {
    label = chainApplies ? 'Chain defaults' : 'Standard';
    detail = 'This step creates a new function. Once the chain is saved you can give it its own settings.';
  } else if (unavailable) {
    label = chainApplies ? 'Chain defaults' : 'Standard';
    detail = "This step uses a function you can't edit (for example a public one).";
  } else if (desc && !desc.is_standard) {
    label = 'Own settings';
    detail = summarizeReliability(desc.settings).join(' · ');
  } else {
    label = chainApplies ? 'Chain defaults' : 'Standard';
    detail = chainApplies
      ? summarizeReliability(chainDesc!.settings).slice(1).join(' · ')
      : "Other allowed models, then general backups. No time limit beyond the chain's.";
  }

  return (
    <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <ShieldOutlinedIcon fontSize="small" color={label === 'Own settings' ? 'primary' : 'action'} />
        <Typography variant="body2" fontWeight={600}>
          Reliability
        </Typography>
        <Chip size="small" variant="outlined" color={label === 'Own settings' ? 'primary' : 'default'} label={label} />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          {detail}
        </Typography>
        {functionId && !unavailable && (
          <Button size="small" onClick={() => setOpen(true)}>
            {desc && !desc.is_standard ? 'Edit' : 'Set for this step'}
          </Button>
        )}
      </Stack>
      {open && functionId && (
        <ReliabilityDialog
          open
          onClose={() => setOpen(false)}
          targetType="function"
          targetId={functionId}
          targetName={functionName || desc?.target.name}
          actAsPublic={actAsPublic}
          onSaved={setDesc}
        />
      )}
    </Box>
  );
};

export default StepReliability;
